(function () {
  "use strict";

  // Centralized copy so we don't rely on Liquid translations inside this script.
  const PRODUCT_CARD_COMPARE_COPY = {
    en: {
      compare: "Compare",
      errorMessage:
        "You have selected items from a different category. Try comparing products from the same category!",
    },
    es: {
      compare: "Comparar",
      errorMessage:
        "Has seleccionado artículos de una categoría diferente. Intenta comparar productos de la misma categoría.",
    },
    fr: {
      compare: "Comparer",
      errorMessage:
        "Vous avez sélectionné des articles d'une catégorie différente. Essayez de comparer des produits de la même catégorie !",
    },
  };

  class ProductCardCompareComponent extends HTMLElement {
    constructor() {
      super();
      this._isConnected = false;
      this._needsRender = false;
      this._renderRAF = null;
      this._lastHTML = null;
      this._product = null;
      this._button = null;

      this._handleButtonClick = this._handleButtonClick.bind(this);
      this._handleCompareProductsChanged =
        this._handleCompareProductsChanged.bind(this);
      this._drawerReadyPromise = null;
    }

    static get observedAttributes() {
      return ["product"];
    }

    get product() {
      return this._product;
    }

    set product(value) {
      this._product = value;
      if (this._isConnected) {
        this._scheduleRender();
      } else {
        this._needsRender = true;
      }
    }

    connectedCallback() {
      this._isConnected = true;
      document.addEventListener(
        "compareProductsChanged",
        this._handleCompareProductsChanged
      );
      if (this._product && this._needsRender) {
        this._scheduleRender();
        this._needsRender = false;
      } else if (this._product && this._lastHTML == null) {
        this._scheduleRender();
      }
    }

    disconnectedCallback() {
      this._isConnected = false;
      document.removeEventListener(
        "compareProductsChanged",
        this._handleCompareProductsChanged
      );
      this._teardownButton();
      if (this._renderRAF) {
        cancelAnimationFrame(this._renderRAF);
        this._renderRAF = null;
      }
    }

    attributeChangedCallback(name, oldVal, newVal) {
      if (name === "product" && newVal && newVal !== oldVal) {
        try {
          const parsed = JSON.parse(newVal);
          this.product = parsed;
        } catch (e) {
          // ignore invalid JSON
        }
      }
    }

    _scheduleRender() {
      if (this._renderRAF) cancelAnimationFrame(this._renderRAF);
      this._renderRAF = requestAnimationFrame(() => {
        this._renderRAF = null;~
        this._doRender();
      });
    }

    _doRender() {
      if (!this._product) {
        this.innerHTML = "";
        this._lastHTML = null;
        this._teardownButton();
        return;
      }

      const safeText = (val) => (val == null ? "" : String(val));
      const escapeAttr = (val) =>
        safeText(val)
          .replace(/&/g, "&amp;")
          .replace(/"/g, "&quot;")
          .replace(/</g, "&lt;");

      const url = this._product?.url || "#";
      const handleURL = new URL(url, window.location.origin);
      const handle = handleURL.pathname.split("/").pop();
      const title = safeText(this._product?.title || "");

      const variants = Array.isArray(this._product?.variants)
        ? this._product.variants
        : [];
      const hasSellableVariant = variants.some(
        (variant) => Boolean(variant?.availableForSale)
      );
      const defaultVariant =
        variants.find((variant) => Boolean(variant?.availableForSale)) ||
        variants[0];
      const defaultVariantId =
        defaultVariant && defaultVariant.id
          ? defaultVariant.id.split("/").pop()
          : "";
      const sellable = variants
        .map((variant) => {
          const id = variant.id?.split("/").pop() || "";
          const available = Boolean(variant.availableForSale);
          return `${id}/${available}`;
        })
        .join(",");

      const isIncluded = this._isProductIncluded(handle);

      const copy = this._getCopy();

      const html = `
        <button
          type="button"
          data-add-compare-product-btn
          data-product-is-sellable="${hasSellableVariant}"
          data-product-handle="${handle}"
          data-is-sellable="${sellable}"
          data-selected-variant-id="${defaultVariantId}"
          data-error-message="${escapeAttr(copy.errorMessage)}"
          aria-label="${escapeAttr(copy.compare)} ${escapeAttr(title)}"
          aria-pressed="${isIncluded}"
          class="inline-flex items-center gap-0.5 right-0 top-0 z-10 hover:cursor-pointer [&.active>.icon-add]:hidden [&.active>.icon-remove]:block"
        >
          <span class="text-custom-grid font-body font-semibold text-xs text-black leading-none">${escapeAttr(copy.compare)}</span>
          <svg xmlns="http://www.w3.org/2000/svg" class="icon icon-action-add block w-4 h-4 text-black icon-add" width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M12.75 11.25V6H11.25V11.25H6V12.75H11.25V18H12.75V12.75H18V11.25H12.75Z" fill="black"></path></svg>
          <svg xmlns="http://www.w3.org/2000/svg" class="icon icon-action-subtract hidden w-4 h-4 text-black icon-remove" width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M6 11.25H18V12.75H6V11.25Z" fill="black"></path></svg>
        </button>
      `;

      if (html !== this._lastHTML) {
        this._teardownButton();
        this.innerHTML = html;
        this._lastHTML = html;
        this._setupButton();
      } else {
        this._syncActiveState();
      }
    }

    _getCopy() {
      const language = this._getLanguage();
      return PRODUCT_CARD_COMPARE_COPY[language] || PRODUCT_CARD_COMPARE_COPY.en;
    }

    _getLanguage() {
      const language = document
        .querySelector(".rcc-search")
        ?.dataset?.language?.toLowerCase();
      if (language && PRODUCT_CARD_COMPARE_COPY[language]) {
        return language;
      }
      return "en";
    }

    _setupButton() {
      const button = this.querySelector("[data-add-compare-product-btn]");
      if (!button) {
        this._button = null;
        return;
      }
      this._button = button;
      this._button.addEventListener("click", this._handleButtonClick, {
        passive: false,
        capture: true,
      });
      this._syncActiveState();
      this._registerButtonWithDrawer();
    }

    _teardownButton() {
      if (!this._button) return;
      this._button.removeEventListener("click", this._handleButtonClick, true);
      this._unregisterButtonFromDrawer();
      this._button = null;
    }

    async _handleButtonClick(event) {
      if (event) {
        if (typeof event.preventDefault === "function") {
          event.preventDefault();
        }
        if (typeof event.stopPropagation === "function") {
          event.stopPropagation();
        }
        if (typeof event.stopImmediatePropagation === "function") {
          event.stopImmediatePropagation();
        }
      }
      if (!this._button) return;
      const button = this._button;
      const handle = button.dataset.productHandle;
      if (!handle) return;

      const wasIncluded = this._isProductIncluded(handle);

      await this._withDrawer(async (drawer) => {
        const ready = await this._waitForDrawerReadyState(drawer);
        if (!ready) return;

        this._attachButtonToDrawer(drawer);

        const resolvedVariant = this._resolveVariant(drawer);
        const variant = this._normalizeVariant(resolvedVariant);
        if (!variant) return;

        if (variant.id) {
          const variantIdFragment = String(variant.id).split("/").pop();
          if (variantIdFragment) {
            this._button.dataset.selectedVariantId = variantIdFragment;
          }
        }

        const compareTarget = { productHandle: handle, variant: {
          ...variant,
          id: +variant.id.replace('gid://shopify/ProductVariant/',''),
          available: variant.availableForSale,
          price: +variant.price.amount * 100,
          compare_at_price: +variant.compareAtPrice.amount * 100,
        } };
        const isSellable = button.dataset.productIsSellable;
        const errorMessage =
          button.dataset.errorMessage ||
          "You cannot compare this product. Please check the comparison rules.";

        if (!wasIncluded) {
          const eligible = await this._ensureEligibility(
            drawer,
            compareTarget,
            errorMessage
          );
          if (!eligible) {
            this._syncActiveState();
            return;
          }
          const added = drawer.addProductToLocalStorage(
            compareTarget,
            isSellable
          );
          if (added) {
            drawer.open();
          }
        } else {
          drawer.removeProductFromLocalStorage(compareTarget);
        }
      });

      this._syncActiveState();
    }

    _handleCompareProductsChanged() {
      this._syncActiveState();
      this._registerButtonWithDrawer();
    }

    _syncActiveState() {
      if (!this._button) return;
      const handle = this._button.dataset.productHandle;
      if (!handle) return;

      const isIncluded = this._isProductIncluded(handle);
      this._button.classList.toggle("active", isIncluded);
      this._button.setAttribute("aria-pressed", String(isIncluded));

      const addIcon = this._button.querySelector(".icon-add");
      const removeIcon = this._button.querySelector(".icon-remove");
      if (addIcon) {
        addIcon.classList.toggle("hidden", isIncluded);
      }
      if (removeIcon) {
        removeIcon.classList.toggle("hidden", !isIncluded);
      }
    }

    _isProductIncluded(handle) {
      const products = this._getCompareProductsState();
      return products.some(
        (product) => product && product.productHandle === handle
      );
    }

    _getCompareProductsState() {
      if (typeof window === "undefined" || !window.localStorage) {
        return [];
      }
      try {
        const stored = window.localStorage.getItem("compareProducts");
        return stored ? JSON.parse(stored) : [];
      } catch (error) {
        console.error("Error parsing compare products", error);
        return [];
      }
    }

    _ensureDrawerReady() {
      if (this._drawerReadyPromise) return this._drawerReadyPromise;
      if (window.customElements && customElements.whenDefined) {
        this._drawerReadyPromise = customElements
          .whenDefined("compared-products")
          .catch(() => {});
      } else {
        this._drawerReadyPromise = Promise.resolve();
      }
      return this._drawerReadyPromise;
    }

    _withDrawer(callback) {
      return this._ensureDrawerReady().then(() => {
        const drawer = this._getDrawer();
        if (!drawer || typeof callback !== "function") return null;
        return callback(drawer);
      });
    }

    _getDrawer() {
      return document.querySelector("compared-products");
    }

    _waitForDrawerReadyState(drawer) {
      const host = drawer || this._getDrawer();
      if (!host) return Promise.resolve(false);
      if (host.rulesManager && Array.isArray(host.products)) {
        return Promise.resolve(true);
      }

      return new Promise((resolve) => {
        let attempts = 0;
        const maxAttempts = 10;
        const step = () => {
          if (host.rulesManager && Array.isArray(host.products)) {
            resolve(true);
            return;
          }
          if (attempts >= maxAttempts) {
            resolve(false);
            return;
          }
          attempts += 1;
          window.setTimeout(step, 50);
        };
        step();
      });
    }

    _registerButtonWithDrawer() {
      if (!this._button) return;
      this._withDrawer((drawer) => {
        this._attachButtonToDrawer(drawer);
      });
    }

    _attachButtonToDrawer(drawer) {
      if (!this._button) return;
      const host = drawer || this._getDrawer();
      if (!host) return;

      const existing = host.addProductButtons
        ? Array.from(host.addProductButtons).filter(Boolean)
        : [];
      if (!existing.includes(this._button)) {
        existing.push(this._button);
      }
      host.addProductButtons = existing;
    }

    _resolveVariant(drawer) {
      if (!this._button) return null;
      const host = drawer || this._getDrawer();
      const fromDrawer =
        host && typeof host.getVariant === "function"
          ? host.getVariant(this._button)
          : null;
      if (fromDrawer) return fromDrawer;

      if (!this._product || !Array.isArray(this._product.variants)) {
        return null;
      }

      const datasetId = this._button.dataset.selectedVariantId;
      const sellableRaw = this._button.dataset.isSellable;
      const fallbackId =
        datasetId ||
        (sellableRaw ? sellableRaw.split(",")[0]?.split("/")[0] : "");

      const foundVariant =
        this._selectVariantFromProduct(fallbackId) ||
        this._product.variants.find(
          (variant) => variant && variant.availableForSale
        ) ||
        this._product.variants[0];

      if (!foundVariant) return null;

      if (typeof foundVariant === "object" && foundVariant !== null) {
        return { ...foundVariant };
      }

      return foundVariant;
    }

    _normalizeVariant(rawVariant) {
      if (!rawVariant) return null;
      if (typeof rawVariant !== "object") {
        return { id: rawVariant };
      }

      const variant = { ...rawVariant };
      const idSource =
        variant.id ||
        variant.variantId ||
        variant.admin_graphql_api_id ||
        variant.legacyResourceId ||
        "";
      variant.id = idSource ? String(idSource) : "";

      variant.sku =
        variant.sku ||
        variant.sku_code ||
        variant.skuCode ||
        variant.variantSku ||
        "";
      if (variant.sku) {
        variant.sku = String(variant.sku);
      }

      const titleSource =
        variant.title ||
        variant.name ||
        variant.variantTitle ||
        variant.product_title ||
        "";
      variant.title = String(titleSource || "");
      if (!variant.name) {
        variant.name = variant.title;
      } else {
        variant.name = String(variant.name);
      }

      const imageSrc =
        (variant.featured_image &&
          (variant.featured_image.src || variant.featured_image.url)) ||
        (variant.image && (variant.image.src || variant.image.url)) ||
        (variant.featuredImage &&
          (variant.featuredImage.src || variant.featuredImage.url)) ||
        variant.image_url ||
        variant.featuredImageUrl ||
        variant.imageSrc ||
        "";

      if (imageSrc) {
        variant.featured_image = {
          ...(typeof variant.featured_image === "object"
            ? variant.featured_image
            : {}),
          src: imageSrc,
        };
        if (
          !variant.image ||
          (typeof variant.image === "object" &&
            !variant.image.src &&
            !variant.image.url)
        ) {
          variant.image = { src: imageSrc, url: imageSrc };
        }
      }

      if (typeof variant.availableForSale !== "boolean") {
        if (typeof variant.available === "boolean") {
          variant.availableForSale = variant.available;
        } else if (typeof variant.isSellable === "boolean") {
          variant.availableForSale = variant.isSellable;
        }
      }

      return variant;
    }

    _selectVariantFromProduct(preferredId) {
      if (!this._product || !Array.isArray(this._product.variants)) {
        return null;
      }

      if (!preferredId) return null;

      return (
        this._product.variants.find((variant) => {
          if (!variant || !variant.id) return false;
          const idFragment = variant.id.split("/").pop();
          return idFragment === preferredId;
        }) || null
      );
    }

    async _ensureEligibility(drawer, compareTarget, errorMessage) {
      if (!drawer || !compareTarget) return false;
      if (
        !drawer.rulesManager ||
        typeof drawer.rulesManager.isEligibleForComparison !== "function"
      ) {
        return true;
      }

      if (
        typeof drawer.rulesManager.getProductData === "function" &&
        compareTarget.productHandle
      ) {
        try {
          await drawer.rulesManager.getProductData(compareTarget.productHandle);
        } catch (error) {
          console.error("Failed to fetch product data for comparison", error);
        }
      }

      let eligible = false;
      try {
        eligible = await drawer.rulesManager.isEligibleForComparison(
          compareTarget
        );
      } catch (error) {
        console.error("Failed to evaluate comparison eligibility", error);
        return false;
      }
      if (!eligible) {
        const message =
          errorMessage ||
          "You cannot compare this product. Please check the comparison rules.";
        if (typeof drawer.displayErrorMessage === "function") {
          drawer.displayErrorMessage(message);
        } else {
          console.warn(message);
        }
      }
      return eligible;
    }

    _unregisterButtonFromDrawer() {
      const drawer = this._getDrawer();
      if (!drawer || !drawer.addProductButtons) return;
      const filtered = Array.from(drawer.addProductButtons).filter(
        (button) => button !== this._button
      );
      drawer.addProductButtons = filtered;
    }
  }

  customElements.define("rcc-product-card-compare", ProductCardCompareComponent);
})();
